import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, Loader2, Plus, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, money2, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export function SaveStayDialog({ hotel, stay, destination, defaultTripId, onClose }) {
  const qc = useQueryClient();
  const { data: trips = [], isLoading } = useQuery({ queryKey: ["trips"], queryFn: async () => (await api.get("/travel/trips")).data });
  const [tripId, setTripId] = useState(defaultTripId || "");
  const [creating, setCreating] = useState(false);
  const [newDest, setNewDest] = useState(destination || "");
  const [saving, setSaving] = useState(false);
  const open = trips.filter((t) => t.status !== "completed");

  const save = async () => {
    setSaving(true);
    try {
      let id = tripId;
      if (creating || !id) {
        const t = await api.post("/travel/trips", { destination: newDest || hotel.name, start_date: stay.checkIn, end_date: stay.checkOut, purpose: "meeting", status: "upcoming", tags: [] });
        id = t.data.id;
      }
      await api.post(`/travel/trips/${id}/stays`, {
        hotel_id: hotel.hotel_id, name: hotel.name, image: hotel.image || "", address: hotel.address || "", star_rating: hotel.star_rating || 0,
        review_score: hotel.review_score || 0, check_in: stay.checkIn, check_out: stay.checkOut, adults: stay.adults, rooms: stay.rooms,
        price_per_night: hotel.price_per_night || 0, total_price: hotel.total_price || 0, currency: hotel.currency || "USD", book_url: hotel.book_url || "",
      });
      toast.success(`${hotel.name} saved to your trip`);
      qc.invalidateQueries({ queryKey: ["trips"] });
      onClose(id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-3xl" data-testid="save-stay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bookmark className="h-5 w-5 text-accent" /> Save stay to a trip</DialogTitle>
          <DialogDescription>{hotel.name} · {stay.checkIn} → {stay.checkOut}{hotel.price_per_night ? ` · ${money2(hotel.price_per_night)}/night` : ""}</DialogDescription>
        </DialogHeader>
        {isLoading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div> : (
          <div className="space-y-2">
            {open.map((t) => (
              <button key={t.id} type="button" onClick={() => { setTripId(t.id); setCreating(false); }} className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${tripId === t.id && !creating ? "bg-accent/10 ring-1 ring-accent" : "bg-secondary/40 hover:bg-secondary/70"}`} data-testid={`save-stay-trip-${t.id}`}>
                <MapPin className="h-4 w-4 shrink-0 text-accent" />
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{t.destination}</span><span className="flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="h-3 w-3" /> {t.start_date || "TBD"}{t.end_date ? ` → ${t.end_date}` : ""}</span></span>
              </button>
            ))}
            <button type="button" onClick={() => { setCreating(true); setTripId(""); }} className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${creating || open.length === 0 ? "bg-accent/10 ring-1 ring-accent" : "bg-secondary/40 hover:bg-secondary/70"}`} data-testid="save-stay-new-trip">
              <Plus className="h-4 w-4 shrink-0 text-accent" /><span className="text-sm font-medium">Create a new trip</span>
            </button>
            {(creating || open.length === 0) && (
              <div className="space-y-1.5 pt-1"><Label>Destination</Label><Input value={newDest} onChange={(e) => setNewDest(e.target.value)} placeholder="e.g. Austin, TX" data-testid="save-stay-new-dest" /></div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} disabled={saving || (!tripId && !creating && open.length > 0)} className="rounded-xl" data-testid="save-stay-confirm">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bookmark className="mr-2 h-4 w-4" />} Save stay</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
